// Icons
import {
  AccountBalance,
  AccountTree,
  AlarmOn,
  Assessment,
  Assignment,
  AssignmentIndOutlined,
  Autorenew,
  Build,
  BuildCircle,
  Business,
  BusinessCenter,
  Description,
  EmojiPeople,
  Engineering,
  FactCheck,
  FolderShared,
  FormatListBulleted,
  Gavel,
  Groups,
  Hardware,
  Home,
  House,
  Landscape,
  LocationCity,
  ManageAccounts,
  ManageSearch,
  Map,
  MapsHomeWork,
  MonetizationOn,
  MenuBook,
  Notes,
  OtherHouses,
  Palette,
  Payment,
  Payments,
  People,
  PeopleOutline,
  Photo,
  PlaylistAddCheck,
  PointOfSale,
  Receipt,
  Schedule,
  Style,
  Subject,
  Summarize,
  SupervisorAccount,
  TouchApp,
  Work,
  CheckBox,
  FileCopy,
  AssignmentTurnedIn,
  List,
  Dashboard,
  Search,
  NoteAlt,
  Transform,
  Send,
  ContentPasteSearch,
  Source,
  Email,
  AssignmentInd,
  BorderColor,
  Widgets,
  SmartButton,
  WebAsset,
  TableChart,
  Title,
  KeyboardBackspace,
  Pattern,
  Pending,
  DynamicFeed,
  Grading,
  Group,
  Print,
  TrackChanges,
  Wallpaper,
  Roofing,
  SsidChart,
  Newspaper,
  RoomPreferences,
  Category,
  Countertops,
  AttachEmail,
  Download,
  LibraryBooks,
  CloudDownload,
  Foundation,
  Handyman,
  Chair,
  Help,
  Feed,
  Article,
  Person,
  Task,
  EventAvailable,
  HistoryEdu,
  ChangeCircle,
  ListAlt,
  DesignServices,
  Hail,
  RequestQuote,
  PaidOutlined,
  Topic,
  BarChart,
  Layers,
  Message,
  ChatBubble
} from '@mui/icons-material';
import { HELP_SLUG, SALES_APP_SLUG, OSC_REPORT_SLUG, isExternalUser } from './user';
import { IUserContext } from '../context/user';
import { INavLink, FeatureFlag, FFProps, UserRoles } from '../models';
const { LegacySystem } = require('../buildSettings.json');

/**
 *
 * @param link INavLink
 * @param context IUserContext
 * @returns boolean
 */
export const hasNavAccess = (roles: string[], context: IUserContext) => {
  // return all of the links
  let hasNeededRoles = true;
  if (
    roles &&
    roles?.length > 0 &&
    (context?.user?.userRoles?.length > 0 || context?.user?.placeOrder?.placeOrderRole)
  ) {
    hasNeededRoles =
      // looks at the link's current roles and see if the logged in user has the same roles so it will show in the menu
      (context?.user?.userRoles?.some(r => roles.includes(r)) ||
        // canEdit means the user has a placeOrderRole of UserRoles.BillingAdmin || UserRoles.FinancialAdmin || UserRoles.GlobalAdmin || 'Warranty Admin'
        context?.canEdit ||
        // look next at the placeOrderRole and see if it matches what it is in the roles: []
        roles.includes(context?.user?.placeOrder?.placeOrderRole?.name)) ??
      false;
  }

  return hasNeededRoles;
};

/**
 *
 * @param link INavLink
 * @param context IUserContext
 * @returns boolean
 */
export const doesUserTypeHaveAccess = (link: INavLink, context: IUserContext) => {
  // We can expand this for user specific restrictions
  if (link.isInternalOnly && isExternalUser(context?.user)) {
    return false;
  }

  return true;
};

export const hasNavActiveFlags = (link: INavLink, flags: FFProps) => {
  let showLink = true; // Return all links
  // If feature flags are set for link, then test the flags.
  if (link?.name === 'Development' && !flags[link.featureFlag]) {
    link.links = [];
  }

  if (link?.featureFlag && !link?.legacyUrl) {
    showLink = flags[link.featureFlag];
  }

  return showLink;
};

export const filterNavItems = (navItems: INavLink[], removalList: string[]) => {
  return navItems.reduce<INavLink[]>((acc, item) => {
    // If the item’s id is in the removal list, skip it
    if (removalList.includes(item.id)) {
      return acc; // do not add it to the accumulator
    }

    // Otherwise, create a shallow copy
    const filteredItem: INavLink = { ...item };

    // If it has children, filter them recursively
    if (item.links && item.links.length > 0) {
      filteredItem.links = filterNavItems(item.links, removalList);
    }

    // Add the filteredItem to the accumulator
    return [...acc, filteredItem];
  }, []);
};

export const filterNavByAllowedRoutes = (
  links: INavLink[],
  allowedRoutes: Set<string>,
  parentPath = ''
): INavLink[] => {
  return links.reduce<INavLink[]>((acc, link) => {
    const currentPath = link.friendlyUrl || (parentPath ? `${parentPath}/${link.name}` : link.name);

    let filteredChildren: INavLink[] = [];
    if (link.links && link.links.length > 0) {
      filteredChildren = filterNavByAllowedRoutes(link.links, allowedRoutes, currentPath);
    }

    // Include the link if its friendly name is allowed or it has allowed children.
    if (allowedRoutes.has(currentPath) || filteredChildren.length) {
      acc.push({ ...link, links: filteredChildren });
    }

    return acc;
  }, []);
};

const extractRoles = (links: INavLink[]) => Array.from(new Set(links.flatMap(link => link.roles)));

export const manageToolsLinks: INavLink[] = [
  {
    name: 'Authorities',
    friendlyUrl: 'Admin/Manage/Authorities',
    icon: <Gavel fontSize="medium" />,
    url: '/admin/manage/authorities',
    legacyUrl: `${LegacySystem}/Portals/Development/ManageAuthorities.aspx`,
    featureFlag: FeatureFlag.v2ManageTools,
    roles: [UserRoles.BLAdmin, UserRoles.BLDevelopment]
  },
  {
    name: 'Counties',
    friendlyUrl: 'Admin/Manage/Counties',
    icon: <AccountBalance fontSize="medium" />,
    url: '/admin/manage/counties',
    featureFlag: FeatureFlag.v2ManageTools,
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'ETL Jobs',
    friendlyUrl: 'Admin/Manage/Etl Jobs',
    icon: <Transform fontSize="medium" />,
    url: '/admin/manage/etl-jobs',
    legacyUrl: `${LegacySystem}/Portals/Admin/EtlJobs.aspx`,
    featureFlag: FeatureFlag.v2ManageTools,
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Municipalities',
    friendlyUrl: 'Admin/Manage/Municipalities',
    icon: <LocationCity fontSize="medium" />,
    url: '/admin/manage/municipalities',
    legacyUrl: `${LegacySystem}/Portals/Development/ManageMunicipalities.aspx`,
    featureFlag: FeatureFlag.v2ManageTools,
    roles: [UserRoles.BLAdmin, UserRoles.BLDevelopment]
  },
  {
    name: 'Plan Styles',
    friendlyUrl: 'Admin/Manage/Plan Styles',
    icon: <Style fontSize="medium" />,
    url: '/admin/manage/plan-styles',
    legacyUrl: `${LegacySystem}/Portals/Development/ManagePlans.aspx`,
    featureFlag: FeatureFlag.v2ManageTools,
    roles: [UserRoles.BLAdmin, UserRoles.BLDevelopment]
  },
  {
    name: 'Plan Rooms',
    friendlyUrl: 'Admin/Manage/Plan Rooms',
    icon: <AccountBalance fontSize="medium" />,
    url: '/admin/manage/plan-rooms',
    featureFlag: FeatureFlag.v2ManageTools,
    roles: [UserRoles.BLAdmin, UserRoles.BLDevelopment]
  },
  {
    name: 'Sage Connectivity Status',
    friendlyUrl: 'Admin/Manage/Sage Connectivity Status',
    icon: <BarChart fontSize="medium" />,
    url: '/admin/manage/sage-connectivity-status',
    featureFlag: FeatureFlag.v2ManageTools,
    roles: [UserRoles.GlobalAdmin]
  },
  {
    name: 'Start Templates',
    friendlyUrl: 'Admin/Manage/Start Templates',
    icon: <Assignment fontSize="medium" />,
    url: '/admin/manage/start-templates',
    legacyUrl: `${LegacySystem}/Portals/Admin/StartTemplates.aspx`,
    featureFlag: FeatureFlag.v2ManageTools,
    roles: [UserRoles.BLAdmin, UserRoles.BLStartSheetTemplates]
  },
  {
    name: 'Territories',
    friendlyUrl: 'Admin/Manage/Territories',
    icon: <Landscape fontSize="medium" />,
    url: '/admin/manage/territories',
    legacyUrl: `${LegacySystem}/Portals/Development/Territories.aspx`,
    featureFlag: FeatureFlag.v2ManageTools,
    roles: [UserRoles.BLAdmin, UserRoles.BLDevelopment]
  },
  {
    name: 'Bulk Option Exclusions',
    friendlyUrl: 'Admin/Manage/Bulk Option Exclusions',
    icon: <Build fontSize="medium" />,
    url: '/admin/manage/bulk-option-exclusions',
    featureFlag: FeatureFlag.bulkOptionExclusions,
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Bulk Option Inclusions',
    friendlyUrl: 'Admin/Manage/Bulk Option Inclusions',
    icon: <Assignment fontSize="medium" />,
    url: '/admin/manage/bulk-option-inclusions',
    featureFlag: FeatureFlag.bulkOptionExclusions,
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Bulk Standard Features',
    friendlyUrl: 'Admin/Manage/Bulk Standard Features',
    icon: <FactCheck fontSize="medium" />,
    url: '/admin/manage/bulk-standard-features',
    featureFlag: FeatureFlag.bulkOptionExclusions,
    roles: [UserRoles.BLAdmin]
  }
];

export const buildLandingLinks: INavLink[] = [
  {
    name: 'Build Utilities',
    friendlyUrl: 'Admin/Build/Build Utilities',
    icon: <Build fontSize="medium" />,
    url: '/admin/build/build-utilities',
    legacyUrl: `${LegacySystem}/Portals/Admin/AdminTools.aspx`,
    featureFlag: FeatureFlag.v2ShowBuildTools,
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Delay Starts',
    friendlyUrl: 'Admin/Build/Delay Starts',
    icon: <AlarmOn fontSize="medium" />,
    url: '/admin/build/delay-starts',
    legacyUrl: `${LegacySystem}/Portals/Admin/AdminTools.aspx`,
    featureFlag: FeatureFlag.v2ShowBuildTools,
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Punch List',
    friendlyUrl: 'Admin/Build/Punch List',
    icon: <Subject fontSize="medium" />,
    url: '/admin/build/punch-list',
    legacyUrl: `${LegacySystem}/Portals/Admin/AdminTools.aspx`,
    featureFlag: FeatureFlag.v2ShowBuildTools,
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Reset Spec',
    friendlyUrl: 'Admin/Build/Reset Spec',
    icon: <Autorenew fontSize="medium" />,
    url: '/admin/build/reset-spec',
    legacyUrl: `${LegacySystem}/Portals/Admin/AdminTools.aspx`,
    featureFlag: FeatureFlag.v2ShowBuildTools,
    roles: [UserRoles.BLAdmin]
  }
];

export const financialLandingLinks: INavLink[] = [
  {
    name: 'ACH Payments',
    friendlyUrl: 'Admin/Financial/Ach',
    icon: <Payments fontSize="medium" />,
    url: '/admin/financial/ach',
    roles: [UserRoles.BLFinancialTools]
  }
];

const productionOrderLinks: INavLink[] = [
  {
    name: 'Order Management',
    friendlyUrl: 'Production/Orders/Order Management',
    url: '/production/orders/order-management',
    icon: <Payments fontSize="medium" />,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLDevelopment,
      UserRoles.BLField,
      UserRoles.BLFieldAdmin,
      UserRoles.BLWarranty
    ]
  },
  {
    name: 'Manage Roles',
    friendlyUrl: 'Production/Orders/Manage Roles',
    url: '/production/orders/manage-roles',
    icon: <Groups fontSize="medium" />,
    roles: [UserRoles.BLAdmin, UserRoles.BillingAdmin, UserRoles.FinancialAdmin]
  },
  {
    name: 'Manage Users',
    friendlyUrl: 'Production/Orders/Manage Users',
    url: '/production/orders/manage-users',
    icon: <ManageAccounts fontSize="medium" />,
    roles: [UserRoles.BLAdmin, UserRoles.BillingAdmin, UserRoles.FinancialAdmin],
    featureFlag: FeatureFlag.hidePlaceOrderPage
  },
  {
    name: 'Order Descriptions',
    friendlyUrl: 'Production/Orders/Order Descriptions',
    icon: <Receipt fontSize="medium" />,
    url: '/production/orders/order-descriptions',
    roles: [UserRoles.BLAdmin, UserRoles.BLDevelopment, UserRoles.BLField, UserRoles.BLFieldAdmin]
  },
  {
    name: 'Order Types',
    friendlyUrl: 'Production/Orders/Order Types',
    icon: <Summarize fontSize="medium" />,
    url: '/production/orders/order-types',
    roles: [UserRoles.BLAdmin, UserRoles.BLDevelopment, UserRoles.BLField, UserRoles.BLFieldAdmin]
  },
  {
    name: 'Place Order',
    friendlyUrl: 'Production/Orders/Place Order',
    icon: <PointOfSale fontSize="medium" />,
    url: '/production/orders/place-order',
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLDevelopment,
      UserRoles.BLField,
      UserRoles.BLFieldAdmin,
      UserRoles.BLWarranty
    ]
  }
];

export const checklistLinks: INavLink[] = [
  {
    name: 'Checklist Templates',
    friendlyUrl: 'Production/Checklists/Checklist Templates',
    icon: <FileCopy fontSize="medium" />,
    url: '/production/checklists/checklist-templates',
    legacyUrl: `${LegacySystem}/Portals/Production/POChecklists.aspx`,
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Manage Checklists',
    friendlyUrl: 'Production/Checklists/Manage Checklists',
    icon: <Summarize fontSize="medium" />,
    url: '/production/checklists/manage-checklists',
    legacyUrl: `${LegacySystem}/Portals/Production/POChecklists.aspx`,
    roles: [UserRoles.BLAdmin, UserRoles.BLField, UserRoles.BLFieldAdmin]
  },
  {
    name: 'Checklist Review',
    friendlyUrl: 'Production/Checklists/Checklist Review',
    icon: <AssignmentTurnedIn fontSize="medium" />,
    url: '/production/checklists/checklist-review',
    legacyUrl: `${LegacySystem}/Portals/Production/POChecklistsReview.aspx`,
    roles: [UserRoles.BLAdmin, UserRoles.BLField, UserRoles.BLFieldAdmin]
  }
];

export const punchlistLinks: INavLink[] = [
  {
    name: 'Dashboard',
    friendlyUrl: 'Production/Punchlists/Dashboard',
    icon: <Dashboard fontSize="medium" />,
    url: '/production/punchlists/dashboard',
    roles: [UserRoles.BLAdmin, UserRoles.BLFieldAdmin, UserRoles.BLField]
  },
  {
    name: 'Punchlist Templates',
    friendlyUrl: 'Production/Punchlists/Punchlist Templates',
    icon: <FileCopy fontSize="medium" />,
    url: '/production/punchlists/punchlist-templates',
    roles: [UserRoles.BLAdmin, UserRoles.BLFieldAdmin, UserRoles.BLField]
  },
  {
    name: 'Reports',
    friendlyUrl: 'Production/Punchlists/Reports',
    icon: <Summarize fontSize="medium" />,
    url: '/production/punchlists/reports',
    roles: [UserRoles.BLAdmin, UserRoles.BLFieldAdmin, UserRoles.BLField]
  },
  {
    name: 'Search',
    friendlyUrl: 'Production/Punchlists/Search',
    icon: <Search fontSize="medium" />,
    url: '/production/punchlists/search',
    roles: [UserRoles.BLAdmin, UserRoles.BLFieldAdmin, UserRoles.BLField]
  }
];

export const optionLinks: INavLink[] = [
  {
    name: 'Manage Options',
    friendlyUrl: 'Development/Options/Manage Options',
    url: '/development/options/manage-options',
    icon: <Payments fontSize="medium" />,
    roles: []
  },
  {
    name: 'Standard Features',
    friendlyUrl: 'Development/Options/Standard Features',
    url: '/development/options/standard-features',
    icon: <Style fontSize="medium" />,
    roles: []
  },
  {
    name: 'Sets and Palettes',
    friendlyUrl: 'Development/Options/Option Sets And Palettes/Option Sets',
    url: '/development/options/option-sets-and-palettes/option-sets',
    icon: <Palette fontSize="medium" />,
    roles: []
  }
];

export const partsLinks: INavLink[] = [
  {
    name: 'Manage Parts',
    friendlyUrl: 'Development/Parts/Manage Parts',
    url: '/development/parts/manage-parts',
    icon: <Foundation fontSize="medium" />,
    roles: []
  },
  {
    name: 'Manage Materials',
    friendlyUrl: 'Development/Parts/Manage Materials',
    url: '/development/parts/manage-materials',
    icon: <Handyman fontSize="medium" />,
    roles: []
  },
  {
    name: 'Manage Part Classes',
    friendlyUrl: 'Development/Parts/Manage Part Classes',
    url: '/development/parts/manage-part-classes',
    icon: <Category fontSize="medium" />,
    roles: []
  }
];

export const checklistsParentLink: INavLink = {
  name: 'Checklists',
  friendlyUrl: 'Production/Checklists',
  url: '/production/checklists/manage-checklists',
  icon: <FactCheck fontSize="medium" />,
  roles: [UserRoles.BLAdmin, UserRoles.BLField, UserRoles.BLFieldAdmin],
  links: checklistLinks,
  featureFlag: FeatureFlag.v2ProductionChecklists
};

export const fieldWalkthroughsManageListLinks: INavLink[] = [
  {
    name: 'Manage Lists',
    friendlyUrl: 'Production/Field Walkthroughs/List Management',
    url: '/production/field-walkthroughs/list-management',
    icon: <ListAlt fontSize="medium" />,
    roles: [UserRoles.BLFieldUserAdmin],
    featureFlag: FeatureFlag.fwtManagement
  }
];

export const fieldWalkthroughsParentLink: INavLink = {
  name: 'Field Walkthroughs',
  friendlyUrl: 'Production/Field Walkthroughs',
  url: '/production/field-walkthroughs',
  legacyUrl: `${LegacySystem}/Portals/Production/FieldWalkthroughs.aspx`,
  icon: <ContentPasteSearch fontSize="medium" />,
  roles: [UserRoles.BLAdmin, UserRoles.BLFieldAdmin, UserRoles.BLField, UserRoles.BLFieldUserAdmin],
  featureFlag: FeatureFlag.v2FieldWalkthroughs,
  links: fieldWalkthroughsManageListLinks
};

const punchlistsParentLink: INavLink = {
  name: 'Punchlists',
  friendlyUrl: 'Production/Punchlists',
  url: '/production/punchlists/dashboard',
  icon: <List fontSize="medium" />,
  roles: [UserRoles.BLAdmin, UserRoles.BLFieldAdmin, UserRoles.BLField],
  links: punchlistLinks,
  featureFlag: FeatureFlag.v2ProductionPunchlists
};

export const planLinks: INavLink[] = [
  {
    name: 'Manage Plans',
    friendlyUrl: 'Development/Plans/Manage Plans',
    url: '/development/plans/manage-plans',
    icon: <FactCheck fontSize="medium" />,
    roles: []
  },
  {
    name: 'Print Plan Series Brochures',
    friendlyUrl: 'Development/Plans/Plan Brochures',
    icon: <Print fontSize="medium" />,
    url: '/development/plans/plan-brochures'
  }
];

export const productionLinks: INavLink[] = [
  {
    name: 'Orders',
    friendlyUrl: 'Production/Orders',
    url: '/production/orders/order-management',
    icon: <ManageSearch fontSize="medium" />,
    roles: extractRoles(productionOrderLinks),
    links: productionOrderLinks
  },
  checklistsParentLink,
  fieldWalkthroughsParentLink,
  punchlistsParentLink,
  {
    name: 'Build Communications',
    friendlyUrl: 'Production/Build Communications',
    icon: <Message fontSize="medium" />,
    url: '/production/build-communications',
    featureFlag: FeatureFlag.buildCommunicationsPage
  },
  {
    name: 'Warranty Messages',
    friendlyUrl: 'Production/Warranty Messages',
    icon: <ChatBubble fontSize="medium" />,
    url: '/production/warranty-messages',
    featureFlag: FeatureFlag.warrantyQuestionPage,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLDevelopment,
      UserRoles.BLField,
      UserRoles.BLFieldAdmin,
      UserRoles.BLWarranty
    ]
  },
  {
    name: 'Site Reports',
    friendlyUrl: 'Production/Site Reports',
    icon: <Summarize fontSize="medium" />,
    legacyUrl: `${LegacySystem}/Portals/Production/SiteReports.aspx`,
    url: '/production/site-reports',
    featureFlag: FeatureFlag.v2SiteReports
  }
];

export const possessionLinks: INavLink[] = [
  {
    name: 'Manage Model Home Furniture',
    friendlyUrl: 'Development/Possessions/Manage Model Home Furniture',
    url: '/development/possessions/manage-model-home-furniture',
    icon: <Chair fontSize="medium" />,
    featureFlag: FeatureFlag.v2Possessions
  },
  {
    name: 'Manage Tools',
    friendlyUrl: 'Development/Possessions/Manage Tools',
    url: '/development/possessions/manage-tools',
    icon: <Handyman fontSize="medium" />,
    featureFlag: FeatureFlag.v2Possessions
  }
];

export const developmentLinks: INavLink[] = [
  {
    name: 'Neighborhoods',
    friendlyUrl: 'Development/Neighborhoods',
    icon: <OtherHouses fontSize="medium" />,
    url: '/development/neighborhoods',
    legacyUrl: `${LegacySystem}/Portals/Development/ManageNeighborhoods.aspx`,
    featureFlag: FeatureFlag.v2Neighborhoods,
    roles: [UserRoles.BLDevelopment, UserRoles.BLAdmin]
  },
  {
    name: 'Plans',
    friendlyUrl: 'Development/Plans',
    icon: <Grading fontSize="medium" />,
    url: '/development/plans/manage-plans',
    legacyUrl: `${LegacySystem}/Portals/Development/ManagePlans.aspx`,
    links: planLinks,
    featureFlag: FeatureFlag.v2Plans,
    roles: [UserRoles.BLDevelopment, UserRoles.BLAdmin]
  },
  {
    name: 'Possessions',
    friendlyUrl: 'Development/Possessions',
    icon: <Business fontSize="medium" />,
    url: '/development/possessions/manage-model-home-furniture',
    legacyUrl: `${LegacySystem}/Portals/Development/Possessions.aspx`,
    roles: [UserRoles.BLDevelopment, UserRoles.BLAdmin],
    links: possessionLinks,
    featureFlag: FeatureFlag.v2Possessions
  },
  {
    name: 'Manage Specs',
    friendlyUrl: 'Development/Specs',
    icon: <FormatListBulleted fontSize="medium" />,
    url: '/development/specs',
    legacyUrl: `${LegacySystem}/Portals/Development/ManageSpecs.aspx`,
    featureFlag: FeatureFlag.v2Specs,
    roles: [UserRoles.BLDevelopment, UserRoles.BLAdmin]
  },
  {
    name: 'Color Schemes',
    friendlyUrl: 'Development/Color Schemes',
    icon: <Palette fontSize="medium" />,
    url: '/development/color-schemes',
    legacyUrl: `${LegacySystem}/Portals/Development/ColorSchemes.aspx`,
    featureFlag: FeatureFlag.v2ColorSchemes,
    roles: [UserRoles.BLDevelopment, UserRoles.BLAdmin]
  },
  {
    name: 'Jobs',
    friendlyUrl: 'Development/Jobs',
    icon: <Work fontSize="medium" />,
    url: '/development/jobs',
    legacyUrl: `${LegacySystem}/Portals/Development/ManageJobs.aspx`,
    featureFlag: FeatureFlag.v2Jobs,
    roles: [UserRoles.BLDevelopment, UserRoles.BLAdmin]
  },
  {
    name: 'Parts',
    friendlyUrl: 'Development/Parts',
    icon: <Countertops fontSize="medium" />,
    url: '/development/parts/manage-parts',
    legacyUrl: `${LegacySystem}/Portals/Development/ManageParts.aspx`,
    featureFlag: FeatureFlag.v2Parts,
    links: partsLinks,
    roles: [UserRoles.BLDevelopment, UserRoles.BLAdmin]
  },
  {
    name: 'Options',
    friendlyUrl: 'Development/Options',
    icon: <TouchApp fontSize="medium" />,
    url: '/development/options/manage-options',
    legacyUrl: `${LegacySystem}/Portals/Development/Options.aspx`,
    featureFlag: FeatureFlag.v2Options,
    links: optionLinks,
    roles: [UserRoles.BLDevelopment, UserRoles.BLAdmin]
  },
  {
    name: 'Reports',
    friendlyUrl: 'Development/Reports',
    icon: <Assessment fontSize="medium" />,
    url: '/development/reports',
    legacyUrl: `${LegacySystem}/Portals/Development/Reports.aspx`,
    featureFlag: FeatureFlag.v2Reports,
    roles: [UserRoles.BLDevelopment, UserRoles.BLAdmin]
  },
  {
    name: 'Model Homes',
    friendlyUrl: 'Development/Model Homes',
    icon: <Home fontSize="medium" />,
    url: '/development/model-homes',
    legacyUrl: `${LegacySystem}/Portals/Development/ModelHomeStartSheetChecklists.aspx`,
    featureFlag: FeatureFlag.v2ModelHomes,
    roles: [UserRoles.BLDevelopment, UserRoles.BLAdmin]
  },
  {
    name: 'Map Setup',
    friendlyUrl: 'Development/Map Setups',
    icon: <Map fontSize="medium" />,
    url: '/development/map-setups',
    legacyUrl: `${LegacySystem}/Portals/Development/MapSetups.aspx`,
    featureFlag: FeatureFlag.v2MapSetup,
    roles: [UserRoles.BLDevelopment, UserRoles.BLAdmin]
  },
  {
    name: 'Photo Approval',
    friendlyUrl: 'Development/Photo Approval',
    icon: <Photo fontSize="medium" />,
    url: '/development/photo-approval',
    legacyUrl: `${LegacySystem}/Portals/Development/PhotoApproval.aspx`,
    featureFlag: FeatureFlag.v2PhotoApproval,
    roles: [UserRoles.BLDevelopment, UserRoles.BLAdmin]
  },
  {
    name: 'Schedule Templates',
    friendlyUrl: 'Development/Schedule Templates',
    icon: <Schedule fontSize="medium" />,
    url: '/development/schedule-templates',
    legacyUrl: `${LegacySystem}/Portals/Development/ScheduleTemplates.aspx`,
    featureFlag: FeatureFlag.v2ScheduleTemplates,
    roles: [UserRoles.BLDevelopment, UserRoles.BLAdmin]
  }
];

export const vendorLinks: INavLink[] = [
  {
    name: 'Invoices',
    friendlyUrl: 'Vendors/Invoices',
    url: '/vendors/invoices',
    icon: <Receipt fontSize="medium" />,
    roles: [UserRoles.VendorInvoiceManagement]
  },
  {
    name: 'Payments',
    friendlyUrl: 'Vendors/Payments',
    url: '/vendors/payments',
    icon: <Payment fontSize="medium" />,
    roles: [UserRoles.VendorInvoiceManagement]
  },
  {
    name: 'Purchase Orders',
    friendlyUrl: 'Vendors/Purchase Orders',
    url: '/vendors/purchase-orders',
    icon: <Payments fontSize="medium" />,
    roles: [UserRoles.VendorPurchaseOrderManagement]
  },
  {
    name: 'Punchlists',
    friendlyUrl: 'Vendors/Punchlists',
    icon: <NoteAlt fontSize="medium" />,
    url: '/vendors/punchlists'
  },
  {
    name: 'Bid Management',
    friendlyUrl: 'Vendors/Bid Management',
    url: '/vendors/bid-management',
    icon: <Notes fontSize="medium" />,
    featureFlag: FeatureFlag.v2ShowTradePartnerBidManagement
  },
  {
    name: 'Contracts',
    friendlyUrl: 'Vendors/Contracts',
    url: '/vendors/contracts',
    icon: <Description fontSize="medium" />,
    roles: [UserRoles.VendorContractsManagement],
    featureFlag: FeatureFlag.v2ShowTradePartnerContracts,
    links: [
      {
        name: 'All Contracts',
        friendlyUrl: 'Vendors/Contracts/All Contracts',
        url: '/vendors/contracts',
        icon: <Description fontSize="medium" />,
        featureFlag: FeatureFlag.v2ShowTradePartnerContracts
      },
      {
        name: 'In-Progress Contracts',
        friendlyUrl: 'Vendors/Contracts/In Progress',
        url: '/vendors/contracts/in-progress',
        icon: <Description fontSize="medium" />,
        featureFlag: FeatureFlag.v2ShowTradePartnerContracts
      }
    ]
  },
  {
    name: 'Vendor Affiliates',
    friendlyUrl: 'Vendors/Vendor Affiliates',
    url: '/vendors/vendor-affiliates',
    icon: <PeopleOutline fontSize="medium" />,
    roles: [UserRoles.BLAdmin, UserRoles.GlobalAdmin]
  },
  {
    name: 'User Management',
    friendlyUrl: 'Vendors/User Management',
    url: '/vendors/user-management',
    icon: <ManageAccounts fontSize="medium" />,
    roles: [UserRoles.VendorUserManagement, UserRoles.BLUserAdmin]
  },
  {
    name: 'Compaction Tests',
    friendlyUrl: 'Vendors/Compaction Tests',
    url: '/vendors/compaction-tests',
    icon: <Description fontSize="medium" />,
    roles: [UserRoles.BLAdmin, UserRoles.VendorCompactionManager, UserRoles.BLCompaction]
  }
];

export const contentManagementLinks: INavLink[] = [
  {
    name: 'Custom Pages',
    friendlyUrl: 'Admin/Manage Content/Custom Pages',
    icon: <Task fontSize="medium" />,
    url: `/admin/manage-content/custom-pages`,
    links: null,
    roles: [UserRoles.ContentManager, UserRoles.BLAdmin]
  },
  {
    name: 'Email Tests',
    friendlyUrl: 'Admin/Manage Content/Email Tests',
    icon: <Send fontSize="medium" />,
    url: '/admin/manage-content/email-tests',
    legacyUrl: `${LegacySystem}/Portals/Admin/AdminTools.aspx`,
    featureFlag: FeatureFlag.v2ManageTools,
    roles: [UserRoles.ContentManager, UserRoles.BLAdmin]
  },
  {
    name: 'Manage Emails',
    friendlyUrl: 'Admin/Manage Content/Emails',
    icon: <Email fontSize="medium" />,
    url: `/admin/manage-content/emails`,
    roles: [UserRoles.ContentManager, UserRoles.BLAdmin]
  },
  {
    name: 'News Management',
    friendlyUrl: 'Admin/Manage Content/News',
    icon: <Newspaper fontSize="medium" />,
    url: `/admin/manage-content/news`,
    roles: [UserRoles.ContentManager, UserRoles.BLAdmin]
  }
];

export const vendorToolsLinks: INavLink[] = [
  {
    name: 'Contract Distribution',
    friendlyUrl: 'Admin/Vendor Tools/Contract Distribution',
    icon: <AttachEmail fontSize="medium" />,
    url: '/admin/vendor-tools/contract-distribution',
    featureFlag: FeatureFlag.v2ShowTradePartnerContracts,
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Contract Templates',
    friendlyUrl: 'Admin/Vendor Tools/Contract Templates',
    icon: <AssignmentIndOutlined fontSize="medium" />,
    url: '/admin/vendor-tools/contract-templates',
    featureFlag: FeatureFlag.v2ShowTradePartnerContracts,
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Content Libraries',
    friendlyUrl: 'Admin/Vendor Tools/Content Libraries',
    icon: <LibraryBooks fontSize="medium" />,
    url: '/admin/vendor-tools/content-libraries',
    featureFlag: FeatureFlag.v2ShowTradePartnerContracts,
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Contract Activity',
    friendlyUrl: 'Admin/Vendor Tools/Contract Activity',
    icon: <PlaylistAddCheck fontSize="medium" />,
    url: '/admin/vendor-tools/contract-activity',
    featureFlag: FeatureFlag.v2ShowTradePartnerContracts,
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Pending Vendors',
    friendlyUrl: 'Admin/Vendor Tools/Pending Vendors',
    icon: <FolderShared fontSize="medium" />,
    url: '/admin/vendor-tools/pending-vendors',
    featureFlag: FeatureFlag.v2ShowTradePartnerContracts,
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Vendors',
    friendlyUrl: 'Admin/Vendor Tools/Vendors',
    icon: <EmojiPeople fontSize="medium" />,
    url: '/admin/vendor-tools/vendors',
    roles: [UserRoles.BLAdmin]
  }
];

export const unifiedUserManagementLinks: INavLink[] = [
  {
    id: 'manage-roles-and-titles',
    name: 'Manage Roles and Titles',
    friendlyUrl: 'Admin/User Tools/Unified User Management/Manage Roles And Titles',
    icon: <Groups fontSize="medium" />,
    url: '/admin/user-tools/unified-user-management/manage-roles-and-titles',
    featureFlag: FeatureFlag.manageAppRolesPage,
    roles: [UserRoles.GlobalAdmin, UserRoles.BLUserAdmin]
  }
];

export const userToolsLinks: INavLink[] = [
  {
    id: 'users',
    name: 'Users',
    friendlyUrl: 'Admin/User Tools/Users',
    icon: <People fontSize="medium" />,
    url: '/admin/user-tools/users',
    roles: [UserRoles.BLAdmin, UserRoles.BLUserAdmin]
  },
  {
    id: 'unified-user-management',
    name: 'Unified User Management',
    friendlyUrl: 'Admin/User Tools/Unified User Management',
    icon: <People fontSize="medium" />,
    links: unifiedUserManagementLinks,
    url: '/admin/user-tools/unified-user-management',
    featureFlag: FeatureFlag.unifiedUserManagement,
    roles: [UserRoles.BLAdmin, UserRoles.BLUserAdmin]
  },
  {
    id: 'attorney-offices',
    name: 'Attorney Offices',
    friendlyUrl: 'Admin/User Tools/Attorney Offices',
    icon: <BusinessCenter fontSize="medium" />,
    url: '/admin/user-tools/attorney-offices',
    legacyUrl: `${LegacySystem}/Portals/Users/Attorneys.aspx`,
    roles: [UserRoles.BLAdmin, UserRoles.BLUserAdmin]
  },
  {
    id: 'framers',
    name: 'Framers',
    friendlyUrl: 'Admin/User Tools/Framers',
    icon: <Hardware fontSize="medium" />,
    url: '/admin/user-tools/framers',
    legacyUrl: `${LegacySystem}/Portals/Users/Framers.aspx`,
    roles: [UserRoles.BLAdmin]
  },
  {
    id: 'project-managers',
    name: 'Project Managers',
    friendlyUrl: 'Admin/User Tools/Project Managers',
    icon: <AccountTree fontSize="medium" />,
    url: '/admin/user-tools/project-managers',
    legacyUrl: `${LegacySystem}/Portals/Users/ProjectManagers.aspx`,
    roles: [UserRoles.BLFieldUserAdmin, UserRoles.BLAdmin]
  },
  {
    id: 'supervisors',
    name: 'Supervisors',
    friendlyUrl: 'Admin/User Tools/Supervisors',
    icon: <Engineering fontSize="medium" />,
    url: '/admin/user-tools/supervisors',
    legacyUrl: `${LegacySystem}/Portals/Users/Supervisors.aspx`,
    roles: [UserRoles.BLFieldUserAdmin, UserRoles.BLAdmin]
  },
  {
    id: 'staff-reassignment',
    name: 'Staff Reassignment',
    friendlyUrl: 'Admin/User Tools/Staff Reassignment',
    icon: <AssignmentInd fontSize="medium" />,
    url: '/admin/user-tools/staff-reassignment',
    legacyUrl: `${LegacySystem}/Portals/Users/StaffReassignment.aspx`,
    roles: [UserRoles.BLFieldUserAdmin, UserRoles.BLAdmin]
  },
  {
    id: 'warranty-managers',
    name: 'Warranty Managers',
    friendlyUrl: 'Admin/User Tools/Warranty Managers',
    icon: <SupervisorAccount fontSize="medium" />,
    url: '/admin/user-tools/warranty-managers',
    legacyUrl: `${LegacySystem}/Portals/Users/WarrantyUsers.aspx`,
    roles: [UserRoles.BLFieldUserAdmin, UserRoles.BLAdmin]
  },
  {
    id: 'foremen',
    name: 'Foremen',
    friendlyUrl: 'Admin/User Tools/Foremen',
    icon: <Hail fontSize="medium" />,
    url: '/admin/user-tools/foremen',
    roles: [UserRoles.BLFieldUserAdmin, UserRoles.BLAdmin]
  }
];

export const contractingLinks: INavLink[] = [
  {
    id: 'clients',
    name: 'Purchasers',
    friendlyUrl: 'Sales/Contracting/Purchasers',
    icon: <Group fontSize="medium" />,
    url: '/sales/contracting/purchasers',
    legacyUrl: `${LegacySystem}/Portals/Sales/ManageClients.aspx`,
    roles: [UserRoles.BLAdmin, UserRoles.BLClient, UserRoles.BLSales, UserRoles.BLAgent]
  },
  {
    id: 'contract-acceptance',
    name: 'Contract Acceptance',
    friendlyUrl: 'Sales/Contracting/Contract Acceptance',
    icon: <Task fontSize="medium" />,
    url: '/sales/contracting/contract-acceptance',
    legacyUrl: `${LegacySystem}/Portals/Sales/ContractAcceptance.aspx`,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLSalesAdmin,
      UserRoles.BLSales,
      UserRoles.BLSalesCoordinator
    ]
  },
  {
    id: 'ten-day-builds',
    name: 'Ten Day Builds',
    friendlyUrl: 'Sales/Contracting/Ten Day Builds',
    icon: <Roofing fontSize="medium" />,
    url: '/sales/contracting/ten-day-builds',
    legacyUrl: `${LegacySystem}/Portals/Sales/TenDayFollowup.aspx`,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLSales,
      UserRoles.BLSalesAdmin,
      UserRoles.BLSalesCoordinator
    ]
  },
  {
    id: 'test-builds',
    name: 'Test Builds',
    friendlyUrl: 'Sales/Contracting/Test Builds',
    icon: <OtherHouses fontSize="medium" />,
    url: '/sales/contracting/test-builds',
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLSales,
      UserRoles.BLSalesAdmin,
      UserRoles.BLSalesCoordinator
    ],
    featureFlag: FeatureFlag.salesTestBuild
  },
  {
    id: 'under-contract',
    name: 'Under Contract',
    friendlyUrl: 'Sales/Contracting/Under Contract',
    icon: <HistoryEdu fontSize="medium" />,
    url: '/sales/contracting/under-contract',
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLSalesAdmin,
      UserRoles.BLSalesCoordinator,
      UserRoles.BLAgent,
      UserRoles.BLSales
    ],
    featureFlag: FeatureFlag.underContract
  },
  {
    id: 'closing-calendar',
    name: 'Closing Calendar',
    friendlyUrl: 'Sales/Contracting/Closing Calendar',
    icon: <EventAvailable fontSize="medium" />,
    url: '/sales/contracting/closing-calendar',
    roles: [UserRoles.BLAdmin, UserRoles.BLClosing, UserRoles.BLSalesCoordinator],
    featureFlag: FeatureFlag.salesClosingCalendar
  },
  {
    id: 'contract-mark-up',
    name: 'Contract Mark-up',
    friendlyUrl: 'Sales/Contracting/Contract Mark-up',
    icon: <DesignServices fontSize="medium" />,
    url: '/sales/contracting/contract-markup',
    roles: [UserRoles.BLAdmin, UserRoles.BLSales],
    featureFlag: FeatureFlag.contractMarkUp
  }
];

export const salesToolsLinks: INavLink[] = [
  {
    id: 'marketing-materials',
    name: 'Marketing Materials',
    friendlyUrl: 'Sales/Sales Tools/Marketing',
    icon: <Wallpaper fontSize="medium" />,
    url: '/sales/sales-tools/marketing',
    legacyUrl: `${LegacySystem}/Portals/Sales/Marketing.aspx`,
    roles: [UserRoles.BLAdmin, UserRoles.BLSales, UserRoles.BLAgent]
  },
  {
    id: 'reports',
    name: 'Reports',
    friendlyUrl: 'Sales/Sales Tools/Reports',
    icon: <SsidChart fontSize="medium" />,
    url: '/sales/sales-tools/reports',
    legacyUrl: `${LegacySystem}/Portals/Sales/SalesReports.aspx`,
    roles: [UserRoles.BLAdmin, UserRoles.BLSales]
  },
  {
    id: 'view-specs',
    name: 'View Specs',
    friendlyUrl: 'Sales/Sales Tools/Specs',
    icon: <FormatListBulleted fontSize="medium" />,
    url: '/sales/sales-tools/specs',
    legacyUrl: `${LegacySystem}/Portals/Sales/ViewSpecs.aspx`,
    roles: [UserRoles.BLAdmin, UserRoles.BLSales]
  },
  {
    id: 'change-series',
    name: 'Change Series',
    friendlyUrl: 'Sales/Sales Tools/Change Series',
    icon: <ChangeCircle fontSize="medium" />,
    url: '/sales/sales-tools/change-series',
    legacyUrl: `${LegacySystem}/Portals/Sales/SalesAdminChangeNeighborhoodSeries.aspx`,
    roles: [UserRoles.BLAdmin, UserRoles.BLSales]
  },
  {
    id: 'option-category-descriptions',
    name: 'Option Category Descriptions',
    friendlyUrl: 'Sales/Sales Tools/Option Category Descriptions',
    icon: <RoomPreferences fontSize="medium" />,
    url: `/sales/sales-tools/option-category-descriptions`,
    legacyUrl: `${LegacySystem}/Portals/Sales/OptionCategoryDescriptions.aspx`,
    links: null,
    roles: [UserRoles.BLAdmin]
  },
  {
    id: 'build-docs',
    name: 'Build Docs',
    friendlyUrl: 'Sales/Sales Tools/Build Docs',
    icon: <FileCopy fontSize="medium" />,
    url: '/documentation/build-documentation',
    legacyUrl: `${LegacySystem}/Portals/Sales/BuildDocumentation.aspx`,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLSales,
      UserRoles.BLSalesAdmin,
      UserRoles.BLSalesCoordinator
    ]
  },
  {
    id: 'sales-management',
    name: 'Sales Management',
    friendlyUrl: 'Sales/Sales Tools/Sales Management',
    icon: <ManageAccounts fontSize="medium" />,
    url: '/sales/sales-tools/sales-management',
    roles: [UserRoles.BLAdmin, UserRoles.BLSalesAdmin]
  },
  {
    id: 'contract-management',
    name: 'Contract Management',
    friendlyUrl: 'Sales/Sales Tools/Contract Management',
    icon: <Topic fontSize="medium" />,
    url: '/sales/sales-tools/contract-management',
    roles: [UserRoles.BLAdmin, UserRoles.BLSales]
  }
];

export const salesLandingLinks: INavLink[] = [
  {
    name: 'Dashboard',
    friendlyUrl: 'Sales/Dashboard',
    icon: <Dashboard fontSize="medium" />,
    url: '/sales/dashboard',
    links: null,
    roles: [UserRoles.BLAdmin, UserRoles.BLSales, UserRoles.BLSalesAdmin, UserRoles.BLAgent]
  },
  {
    name: 'Contracting',
    friendlyUrl: 'Sales/Contracting',
    icon: <Receipt fontSize="medium" />,
    url: null,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLSales,
      UserRoles.BLSalesAdmin,
      UserRoles.BLSalesCoordinator
    ],
    links: contractingLinks
  },
  {
    name: 'Tools',
    friendlyUrl: 'Sales/Sales Tools',
    icon: <PointOfSale fontSize="medium" />,
    url: null,
    links: salesToolsLinks,
    roles: [UserRoles.BLAdmin, UserRoles.BLSales, UserRoles.BLAgent]
  }
];

export const adminLandingLinks: INavLink[] = [
  {
    name: 'Manage Tools',
    friendlyUrl: 'Admin/Manage Tools',
    url: null,
    icon: <Notes fontSize="medium" />,
    links: manageToolsLinks,
    roles: extractRoles(manageToolsLinks)
  },
  {
    name: 'Build Tools',
    friendlyUrl: 'Admin/Build Tools',
    url: null,
    icon: <Build fontSize="medium" />,
    links: buildLandingLinks,
    roles: extractRoles(buildLandingLinks)
  },
  {
    name: 'Financial Tools',
    friendlyUrl: 'Admin/Financial Tools',
    url: null,
    icon: <AccountBalance fontSize="medium" />,
    links: financialLandingLinks,
    roles: extractRoles(financialLandingLinks)
  },
  {
    name: 'Content Management',
    friendlyUrl: 'Admin/Manage Content',
    url: null,
    icon: <Source fontSize="medium" />,
    links: contentManagementLinks,
    roles: extractRoles(contentManagementLinks)
  },
  {
    name: 'Vendor Tools',
    friendlyUrl: 'Admin/Vendor Tools',
    url: null,
    icon: <Engineering fontSize="medium" />,
    links: vendorToolsLinks,
    roles: extractRoles(vendorToolsLinks)
  },
  {
    name: 'User Tools',
    friendlyUrl: 'Admin/User Tools',
    url: null,
    icon: <Person fontSize="medium" />,
    links: userToolsLinks,
    roles: extractRoles(userToolsLinks)
  }
];

export const homeownerPortalLinks: INavLink[] = [
  {
    name: 'Classic Portal',
    friendlyUrl: 'Custom Pages/Homeowner Portal',
    url: '/custom-pages/homeowner-portal',
    icon: <Feed fontSize="medium" />,
    links: null
  },
  {
    name: 'Thrive Portal',
    friendlyUrl: 'Custom Pages/Homeowner Portal Thrive',
    url: '/custom-pages/homeowner-portal-thrive',
    icon: <Article fontSize="medium" />,
    links: null
  }
];

export const buildDocumentationLinks: INavLink[] = [
  {
    id: 'file-management',
    name: 'File Management',
    friendlyUrl: 'Documentation/Build Documentation/File Management',
    icon: <Source fontSize="medium" />,
    url: '/documentation/build-documentation/file-management',
    featureFlag: FeatureFlag.documentationBuildDocumentation,
    roles: [UserRoles.BLAdmin, UserRoles.BLClosing]
  },
  {
    id: 'manage-document-groups',
    name: 'Manage Document Groups',
    friendlyUrl: 'Documentation/Build Documentation/Manage Document Groups',
    icon: <Source fontSize="medium" />,
    url: '/documentation/build-documentation/manage-document-groups',
    featureFlag: FeatureFlag.documentationBuildDocumentation,
    roles: [UserRoles.BLAdmin, UserRoles.BLClosing]
  }
];

export const documentationLinks: INavLink[] = [
  {
    name: 'Build Documentation',
    friendlyUrl: 'Documentation/Build Documentation',
    legacyUrl: `${LegacySystem}/Portals/Documentation/Documentation.aspx`,
    url: '/documentation/build-documentation',
    icon: <Article fontSize="medium" />,
    links: buildDocumentationLinks,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLField,
      UserRoles.BLFieldAdmin,
      UserRoles.BLClosing,
      UserRoles.BLSales,
      UserRoles.BLSalesAdmin,
      UserRoles.BLSalesCoordinator
    ],
    featureFlag: FeatureFlag.documentationBuildDocumentation
  },
  {
    id: 'build-selected-options',
    name: 'Build Selected Options',
    friendlyUrl: 'Documentation/Build Selected Options',
    url: '/documentation/build-selected-options',
    icon: <CheckBox fontSize="medium" />,
    links: null,
    roles: [UserRoles.BLAdmin, UserRoles.BLField, UserRoles.BLFieldAdmin]
  },
  {
    id: 'compaction-tests',
    name: 'Compaction Tests',
    friendlyUrl: 'Vendors/Compaction Tests',
    url: '/vendors/compaction-tests',
    icon: <Layers fontSize="medium" />,
    links: null,
    roles: [UserRoles.BLAdmin, UserRoles.BLCompaction]
  }
];

export const reportingSalesLinks: INavLink[] = [
  {
    name: 'Sales App',
    friendlyUrl: 'Reporting/Sales/Sales App',
    icon: <PaidOutlined fontSize="medium" />,
    url: `/custom-pages/${SALES_APP_SLUG}`,
    isInternalOnly: true,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLDevelopment,
      UserRoles.BLField,
      UserRoles.BLFieldAdmin,
      UserRoles.BLFieldUserAdmin,
      UserRoles.BLFinance,
      UserRoles.BLClosing,
      UserRoles.BLProjectManager,
      UserRoles.BLReporting,
      UserRoles.BLSales,
      UserRoles.BLSalesAdmin,
      UserRoles.BLUserAdmin,
      UserRoles.BLWarranty,
      UserRoles.BillingAdmin,
      UserRoles.FinancialAdmin,
      UserRoles.ContentManager,
      UserRoles.GlobalAdmin,
      UserRoles.WarrantyAdmin
    ]
  },
  {
    name: 'OSC Report',
    friendlyUrl: 'Reporting/Sales/OSC Report',
    icon: <BarChart fontSize="medium" />,
    url: `/custom-pages/${OSC_REPORT_SLUG}`,
    isInternalOnly: true,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLDevelopment,
      UserRoles.BLField,
      UserRoles.BLFieldAdmin,
      UserRoles.BLFieldUserAdmin,
      UserRoles.BLFinance,
      UserRoles.BLClosing,
      UserRoles.BLProjectManager,
      UserRoles.BLReporting,
      UserRoles.BLSales,
      UserRoles.BLSalesAdmin,
      UserRoles.BLUserAdmin,
      UserRoles.BLWarranty,
      UserRoles.BillingAdmin,
      UserRoles.FinancialAdmin,
      UserRoles.ContentManager,
      UserRoles.GlobalAdmin,
      UserRoles.WarrantyAdmin
    ]
  }
];

export const reportingLinks: INavLink[] = [
  {
    name: 'Activity Tracking',
    friendlyUrl: 'Reporting/Activity Tracking',
    icon: <TrackChanges fontSize="medium" />,
    url: '/reporting/activity-tracking',
    roles: [UserRoles.GlobalAdmin, UserRoles.BLActivityTracking]
  },
  {
    name: 'Undefined Parts',
    friendlyUrl: 'Reporting/Undefined Parts',
    icon: <BuildCircle fontSize="medium" />,
    url: '/reporting/undefined-parts',
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Vendor Contract Activity',
    friendlyUrl: 'Reporting/Vendor Contract Activity',
    icon: <BorderColor fontSize="medium" />,
    url: '/reporting/vendor-contract-activity',
    roles: [UserRoles.BLAdmin]
  },
  {
    name: 'Sales',
    friendlyUrl: 'Reporting/Sales',
    icon: <RequestQuote fontSize="medium" />,
    url: null,
    links: reportingSalesLinks,
    isInternalOnly: true,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLDevelopment,
      UserRoles.BLField,
      UserRoles.BLFieldAdmin,
      UserRoles.BLFieldUserAdmin,
      UserRoles.BLFinance,
      UserRoles.BLClosing,
      UserRoles.BLProjectManager,
      UserRoles.BLReporting,
      UserRoles.BLSales,
      UserRoles.BLSalesAdmin,
      UserRoles.BLUserAdmin,
      UserRoles.BLWarranty,
      UserRoles.BillingAdmin,
      UserRoles.FinancialAdmin,
      UserRoles.ContentManager,
      UserRoles.GlobalAdmin,
      UserRoles.WarrantyAdmin
    ]
  }
];

export const AdminPortal: INavLink = {
  name: 'Admin',
  friendlyUrl: 'Admin',
  icon: <SupervisorAccount fontSize="medium" />,
  url: `/admin`,
  links: adminLandingLinks,
  roles: extractRoles(adminLandingLinks)
};

export const mainNavLinks: INavLink[] = [
  AdminPortal,
  {
    name: 'Development',
    friendlyUrl: 'Development',
    icon: <MapsHomeWork fontSize="medium" />,
    legacyUrl: `${LegacySystem}/Portals/Development/DevelopmentHome.aspx`,
    url: '/development',
    featureFlag: FeatureFlag.v2Development,
    links: developmentLinks,
    roles: [UserRoles.BLAdmin, UserRoles.BLDevelopment]
  },
  {
    name: 'Documentation',
    friendlyUrl: 'Documentation',
    icon: <MenuBook fontSize="medium" />,
    url: '/documentation',
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLField,
      UserRoles.BLFieldAdmin,
      UserRoles.BLClosing,
      UserRoles.BLCompaction,
      UserRoles.BLSales,
      UserRoles.BLSalesAdmin,
      UserRoles.BLSalesCoordinator
    ],
    links: documentationLinks
  },
  {
    name: 'Production',
    friendlyUrl: 'Production',
    icon: <Build fontSize="medium" />,
    url: '/production',
    links: productionLinks,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLDevelopment,
      UserRoles.BLField,
      UserRoles.BLFieldAdmin,
      UserRoles.BLWarranty
    ]
  },
  {
    id: 'reporting',
    name: 'Reporting',
    friendlyUrl: 'Reporting',
    icon: <Schedule fontSize="medium" />,
    legacyUrl: `${LegacySystem}/Portals/Reporting/GlobalReports.aspx`,
    url: '',
    links: reportingLinks,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLReporting,
      UserRoles.BLSales,
      UserRoles.BLDevelopment,
      UserRoles.BLFieldAdmin,
      UserRoles.BLFinance,
      UserRoles.BLWarranty,
      UserRoles.BLField,
      UserRoles.BLFieldUserAdmin,
      UserRoles.BLClosing,
      UserRoles.BLProjectManager,
      UserRoles.BLSalesAdmin,
      UserRoles.BLUserAdmin,
      UserRoles.BillingAdmin,
      UserRoles.FinancialAdmin,
      UserRoles.ContentManager,
      UserRoles.GlobalAdmin,
      UserRoles.WarrantyAdmin,
      UserRoles.BLActivityTracking
    ]
  },
  {
    name: 'Sales',
    friendlyUrl: 'Sales',
    icon: <MonetizationOn fontSize="medium" />,
    url: `/sales`,
    legacyUrl: `${LegacySystem}/Portals/Sales/SalesHome.aspx`,
    links: salesLandingLinks,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLSales,
      UserRoles.BLSalesAdmin,
      UserRoles.BLSalesCoordinator,
      UserRoles.BLAgent
    ]
  },
  {
    name: 'Vendors',
    friendlyUrl: 'Vendors',
    icon: <FolderShared fontSize="medium" />,
    url: `/vendors`,
    links: vendorLinks,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLSubcontractor,
      UserRoles.BLVendor,
      UserRoles.VendorInvoiceManagement,
      UserRoles.VendorPurchaseOrderManagement,
      UserRoles.VendorContractsManagement,
      UserRoles.VendorUserManagement,
      UserRoles.VendorCompactionManager,
      UserRoles.BLCompaction
    ]
  },
  {
    name: 'Warranty',
    friendlyUrl: 'Warranty',
    icon: <Notes fontSize="medium" />,
    legacyUrl: `${LegacySystem}/Portals/Warranty/WarrantyHome.aspx`,
    url: '',
    links: null,
    roles: [UserRoles.BLAdmin, UserRoles.BLWarranty]
  },
  {
    id: 'homeowner-portals',
    name: 'Homeowner Portals',
    friendlyUrl: 'Custom Pages/Homeowner Portal',
    icon: <Home fontSize="medium" />,
    url: `/homeowner-portals`,
    links: homeownerPortalLinks,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLDevelopment,
      UserRoles.BLField,
      UserRoles.BLFieldAdmin,
      UserRoles.BLFieldUserAdmin,
      UserRoles.BLFinance,
      UserRoles.BLProjectManager,
      UserRoles.BLReporting,
      UserRoles.BLSales,
      UserRoles.BLSalesAdmin,
      UserRoles.BLUserAdmin,
      UserRoles.BLWarranty,
      UserRoles.BillingAdmin,
      UserRoles.FinancialAdmin,
      UserRoles.ContentManager,
      UserRoles.GlobalAdmin,
      UserRoles.WarrantyAdmin
    ]
  },
  {
    id: 'homeowner-portal-v2',
    name: 'Homeowner Portal',
    friendlyUrl: 'Homeowner Portal',
    icon: <Home fontSize="medium" />,
    url: `/homeowner-portal`,
    links: null,
    featureFlag: FeatureFlag.homeownerPortalv2,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLDevelopment,
      UserRoles.BLField,
      UserRoles.BLFieldAdmin,
      UserRoles.BLFieldUserAdmin,
      UserRoles.BLFinance,
      UserRoles.BLProjectManager,
      UserRoles.BLReporting,
      UserRoles.BLSales,
      UserRoles.BLSalesAdmin,
      UserRoles.BLUserAdmin,
      UserRoles.BLWarranty,
      UserRoles.BillingAdmin,
      UserRoles.FinancialAdmin,
      UserRoles.ContentManager,
      UserRoles.GlobalAdmin,
      UserRoles.WarrantyAdmin
    ]
  },
  {
    id: 'how-tos',
    name: "How-To's",
    friendlyUrl: 'Custom Pages/How To',
    icon: <Help fontSize="medium" />,
    url: `/custom-pages/${HELP_SLUG}`,
    links: null,
    isInternalOnly: true,
    roles: [
      UserRoles.BLAdmin,
      UserRoles.BLDevelopment,
      UserRoles.BLField,
      UserRoles.BLFieldAdmin,
      UserRoles.BLFieldUserAdmin,
      UserRoles.BLFinance,
      UserRoles.BLClosing,
      UserRoles.BLProjectManager,
      UserRoles.BLReporting,
      UserRoles.BLSales,
      UserRoles.BLSalesAdmin,
      UserRoles.BLUserAdmin,
      UserRoles.BLWarranty,
      UserRoles.BillingAdmin,
      UserRoles.FinancialAdmin,
      UserRoles.ContentManager,
      UserRoles.GlobalAdmin,
      UserRoles.WarrantyAdmin
    ]
  }
];

export const developmentDrawerLinks: INavLink[] = [
  {
    name: 'Territories',
    friendlyUrl: 'Development/Territories',
    icon: <Landscape fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/Territories.aspx`,
    links: null
  },
  {
    name: 'Municipalities',
    friendlyUrl: 'Development/Municipalities',
    icon: <LocationCity fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/ManageMunicipalities.aspx`,
    links: null
  },
  {
    name: 'Authorities',
    friendlyUrl: 'Development/Authorities',
    icon: <Gavel fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/ManageAuthorities.aspx`,
    links: null
  },
  {
    name: 'Neighborhoods',
    friendlyUrl: 'Development/Neighborhoods',
    icon: <House fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/ManageNeighborhoods.aspx`,
    links: null
  },
  {
    name: 'Jobs',
    friendlyUrl: 'Development/Jobs',
    icon: <Work fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/ManageJobs.aspx`,
    links: null
  },
  {
    name: 'Plans',
    friendlyUrl: 'Development/Plans',
    icon: <PlaylistAddCheck fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/ManagePlans.aspx`,
    links: null
  },
  {
    name: 'Parts',
    friendlyUrl: 'Development/Parts',
    icon: <Build fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/ManageParts.aspx`,
    links: null
  },
  {
    name: 'Options',
    friendlyUrl: 'Development/Options',
    icon: <TouchApp fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/Options.aspx`,
    links: null
  },
  {
    name: 'Manage Specs',
    friendlyUrl: 'Development/Specs',
    icon: <FormatListBulleted fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/ManageSpecs.aspx`,
    links: null
  },
  {
    name: 'Reports',
    friendlyUrl: 'Development/Reports',
    icon: <Assessment fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/Reports.aspx`,
    links: null
  },
  {
    name: 'Clr Schemes',
    friendlyUrl: 'Development/Color Schemes',
    icon: <Palette fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/ColorSchemes.aspx`,
    links: null
  },
  {
    name: 'Possessions',
    friendlyUrl: 'Development/Possessions',
    icon: <Business fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/Possessions.aspx`,
    links: null
  },
  {
    name: 'Model Homes',
    friendlyUrl: 'Development/Model Homes',
    icon: <Home fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/ModelHomeStartSheetChecklists.aspx`,
    links: null
  },
  {
    name: 'Map Setup',
    friendlyUrl: 'Development/Map Setups',
    icon: <Map fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/MapSetups.aspx`,
    links: null
  },
  {
    name: 'Photo Approval',
    friendlyUrl: 'Development/Photo Approval',
    icon: <Photo fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/PhotoApproval.aspx`,
    links: null
  },
  {
    name: 'Schedule Templates',
    friendlyUrl: 'Development/Schedule Templates',
    icon: <Schedule fontSize="medium" />,
    url: `${LegacySystem}/Portals/Development/ScheduleTemplates.aspx`,
    links: null
  }
];

// Links commented out below are intended for phase 2 expansion of the Kitchen Sink
export const styleGuideLinks: INavLink[] = [
  {
    name: 'Kitchen Sink',
    friendlyUrl: 'Kitchen Sink',
    icon: <Style fontSize="medium" />,
    url: `/kitchen-sink`,
    roles: []
  },
  {
    name: 'Components',
    friendlyUrl: 'Kitchen Sink/Components',
    icon: <Widgets fontSize="medium" />,
    url: '',
    links: [
      // {
      //   name: 'Accordions',
      //   url: `/kitchen-sink/components/accordions`,
      //   icon: <HorizontalSplit fontSize="medium" />,
      //   links: null,
      //   roles: []
      // },
      {
        name: 'Buttons',
        friendlyUrl: 'Kitchen Sink/Components/Buttons',
        url: `/kitchen-sink/components/buttons`,
        icon: <SmartButton fontSize="medium" />,
        links: [
          {
            name: 'CSV Download Button',
            friendlyUrl: 'Kitchen Sink/Components/Buttons/CSV Download Button',
            url: `/kitchen-sink/components/buttons/csv-download`,
            icon: <CloudDownload fontSize="medium" />,
            roles: []
          }
        ],
        roles: []
      },
      {
        name: 'Cards',
        friendlyUrl: 'Kitchen Sink/Components/Cards',
        url: `/kitchen-sink/components/cards`,
        icon: <WebAsset fontSize="medium" />,
        roles: []
      },
      {
        name: 'Grids',
        friendlyUrl: 'Kitchen Sink/Components/Grids',
        url: `/kitchen-sink/components/grids`,
        icon: <TableChart fontSize="medium" />,
        links: [
          // {
          //   name: 'Table',
          //   url: `/kitchen-sink/components/grids/table`,
          //   icon: <TableRows fontSize="medium" />,
          //   roles: []
          // },
          {
            name: 'Bulk Download Grid',
            friendlyUrl: 'Kitchen Sink/Components/Grids/Bulk Download Grid',
            url: `/kitchen-sink/components/grids/bulk-download-grid`,
            icon: <Download fontSize="medium" />,
            roles: []
          }
          // {
          //   name: 'Tabs',
          //   url: `/kitchen-sink/components/grids/tabs`,
          //   icon: <TabUnselected fontSize="medium" />,
          //   links: null,
          //   roles: []
          // },
          // {
          //   name: 'Filters',
          //   url: `/kitchen-sink/components/grids/filters`,
          //   icon: <FilterAlt fontSize="medium" />,
          //   links: null,
          //   roles: []
          // },
          // {
          //   name: 'Pagination',
          //   url: `/kitchen-sink/components/grids/pagination`,
          //   icon: <SettingsEthernet fontSize="medium" />,
          //   links: null,
          //   roles: []
          // }
        ],
        roles: []
      },
      // {
      //   name: 'Inputs',
      //   url: null,
      //   icon: <Dns fontSize="medium" />,
      //   links: [
      //     // {
      //     //   name: 'Text Field',
      //     //   icon: <TextFields fontSize="medium" />,
      //     //   url: `/kitchen-sink/components/inputs/text`,
      //     //   links: null,
      //     //   roles: []
      //     // },
      //     // {
      //     //   name: 'Phone Text Field',
      //     //   icon: <SettingsPhone fontSize="medium" />,
      //     //   url: `/kitchen-sink/components/inputs/phone-text-field`,
      //     //   links: null,
      //     //   roles: []
      //     // },
      //     // {
      //     //   name: 'Textarea',
      //     //   icon: <AspectRatio fontSize="medium" />,
      //     //   url: `/kitchen-sink/components/inputs/textarea`,
      //     //   links: null,
      //     //   roles: []
      //     // },
      //     // {
      //     //   name: 'Select',
      //     //   icon: <Segment fontSize="medium" />,
      //     //   url: `/kitchen-sink/components/inputs/select`,
      //     //   links: null,
      //     //   roles: []
      //     // },
      //     // {
      //     //   name: 'Autocomplete',
      //     //   icon: <EditAttributes fontSize="medium" />,
      //     //   url: `/kitchen-sink/components/inputs/autocomplete`,
      //     //   links: null,
      //     //   roles: []
      //     // }
      //     // {
      //     //   name: 'Date Picker',
      //     //   icon: <CalendarToday fontSize="medium" />,
      //     //   url: `/kitchen-sink/components/inputs/date-picker`,
      //     //   links: null,
      //     //   roles: []
      //     // },
      //     // {
      //     //   name: 'Date Range Picker',
      //     //   icon: <DateRange fontSize="medium" />,
      //     //   url: `/kitchen-sink/components/inputs/date-range-picker`,
      //     //   links: null,
      //     //   roles: []
      //     // },
      //     // {
      //     //   name: 'Checkbox',
      //     //   icon: <CheckBox fontSize="medium" />,
      //     //   url: `/kitchen-sink/components/inputs/checkbox`,
      //     //   links: null,
      //     //   roles: []
      //     // },
      //     // {
      //     //   name: 'Radio Buttons',
      //     //   icon: <RadioButtonChecked fontSize="medium" />,
      //     //   url: `/kitchen-sink/components/inputs/radio-buttons`,
      //     //   links: null,
      //     //   roles: []
      //     // },
      //     // {
      //     //   name: 'Switches / Toggles',
      //     //   icon: <ToggleOn fontSize="medium" />,
      //     //   url: `/kitchen-sink/components/inputs/switches`,
      //     //   links: null,
      //     //   roles: []
      //     // },
      //     // {
      //     //   name: 'Image Upload',
      //     //   icon: <AddPhotoAlternate fontSize="medium" />,
      //     //   url: `/kitchen-sink/components/inputs/image-upload`,
      //     //   links: null,
      //     //   roles: []
      //     // }
      //   ],
      //   roles: []
      // },
      {
        name: 'Loader',
        friendlyUrl: 'Kitchen Sink/Components/Loader',
        url: `/kitchen-sink/components/loader`,
        icon: <Pending fontSize="medium" />,
        roles: []
      },
      {
        name: 'Modals',
        friendlyUrl: 'Kitchen Sink/Components/Modals',
        url: `/kitchen-sink/components/modals`,
        icon: <DynamicFeed fontSize="medium" />,
        roles: []
      },
      {
        name: 'Page',
        friendlyUrl: 'Kitchen Sink/Components/Page',
        url: `/kitchen-sink/components/page`,
        icon: <Description fontSize="medium" />,
        roles: []
      },
      {
        name: 'Page Title',
        friendlyUrl: 'Kitchen Sink/Components/Page Title',
        url: `/kitchen-sink/components/page-title`,
        icon: <Title fontSize="medium" />,
        roles: []
      },
      {
        name: 'Page Title w/ Back',
        friendlyUrl: 'Kitchen Sink/Components/Page Title w/ Back',
        url: `/kitchen-sink/components/page-title-with-back`,
        icon: <KeyboardBackspace fontSize="medium" />,
        roles: []
      }
      // {
      //   name: 'Tabs',
      //   url: `/kitchen-sink/components/tabs`,
      //   icon: <Tab fontSize="medium" />,
      //   links: null,
      //   roles: []
      // }
    ],
    roles: []
  },
  {
    name: 'UI/UX Patterns',
    friendlyUrl: 'Kitchen Sink/UI UX Patterns',
    url: `/kitchen-sink/ui-ux-patterns`,
    icon: <Pattern fontSize="medium" />,
    links: null,
    roles: []
  }
];

export const filterNavLinksForAccess = (
  routes: INavLink[],
  userContext: IUserContext,
  featureFlags: FFProps,
  allowedRoutes?: Set<string>
): INavLink[] => {
  let filtered: INavLink[];

  // If the feature flag is enabled, filter the nav links by the allowed routes
  if (featureFlags?.authorizeUserNavigationAccess && allowedRoutes && allowedRoutes.size > 0) {
    filtered = filterNavByAllowedRoutes(routes, allowedRoutes);
  } else {
    filtered = routes.filter(link => hasNavAccess(link.roles, userContext));
  }

  // Special filtering for Financial Tools, must have BLFinancialTools role to see it
  filtered = filtered.filter(link => {
    if (link.name === 'Financial Tools') {
      return userContext?.user?.userRoles?.includes(UserRoles.BLFinancialTools) ?? false;
    }
    return true;
  });

  return filtered
    .filter(item => !!hasNavActiveFlags(item, featureFlags))
    .filter(link => doesUserTypeHaveAccess(link, userContext));
};
